// Camera placeholder. When the user opens the browser source with `?dev=1`,
// renders a faint outline + "CAMERA" label in the reserved 480x270 area so the
// streamer can verify positioning before going live. In production the area is
// fully transparent; OBS composites the real webcam over it.

import {
  Mesh,
  MeshBasicMaterial,
  Object3D,
  PlaneGeometry,
} from 'three';
import type { CameraRect } from './lib/safe-area.js';
import { textTexture } from './lib/text-texture.js';
import { COLOR_PANEL_BORDER, FONT_UI } from './lib/theme.js';

export interface CameraPlaceholderHandle {
  group: Object3D;
  setRect: (rect: CameraRect | null) => void;
  setVisible: (visible: boolean) => void;
  dispose: () => void;
}

const STROKE = 4;

export function createCameraPlaceholder(): CameraPlaceholderHandle {
  const group = new Object3D();
  group.name = 'camera-placeholder';
  group.visible = false;

  // Four thin border bars (top/bottom/left/right) sized at runtime via setRect.
  const borderMat = new MeshBasicMaterial({ color: COLOR_PANEL_BORDER, transparent: true, opacity: 0.75 });
  const top = new Mesh(new PlaneGeometry(1, STROKE), borderMat);
  const bot = new Mesh(new PlaneGeometry(1, STROKE), borderMat);
  const left = new Mesh(new PlaneGeometry(STROKE, 1), borderMat);
  const right = new Mesh(new PlaneGeometry(STROKE, 1), borderMat);
  group.add(top, bot, left, right);

  const labelMat = new MeshBasicMaterial({ transparent: true, depthWrite: false });
  const label = new Mesh(new PlaneGeometry(180, 36), labelMat);
  label.position.z = 0.1;
  group.add(label);

  function setRect(rect: CameraRect | null): void {
    if (!rect) {
      group.visible = false;
      return;
    }
    const cx = rect.x + rect.width / 2;
    const cy = rect.y - rect.height / 2;
    top.scale.x = rect.width;
    bot.scale.x = rect.width;
    left.scale.y = rect.height;
    right.scale.y = rect.height;
    top.position.set(cx, rect.y - STROKE / 2, 0);
    bot.position.set(cx, rect.y - rect.height + STROKE / 2, 0);
    left.position.set(rect.x + STROKE / 2, cy, 0);
    right.position.set(rect.x + rect.width - STROKE / 2, cy, 0);

    if (labelMat.map) labelMat.map.dispose();
    labelMat.map = textTexture('CAMERA', {
      font: `28px ${FONT_UI}`,
      color: '#FFFFFF',
      width: 180,
      height: 36,
      weight: 'bold',
    });
    labelMat.needsUpdate = true;
    label.position.x = cx;
    label.position.y = cy;
  }

  return {
    group,
    setRect,
    setVisible(v: boolean) {
      group.visible = v;
    },
    dispose() {
      borderMat.dispose();
      top.geometry.dispose();
      bot.geometry.dispose();
      left.geometry.dispose();
      right.geometry.dispose();
      labelMat.dispose();
      label.geometry.dispose();
    },
  };
}
